/**
 * Product Reviews Modal
 *
 * Controls two alternative click behaviors on the product page reviews badge:
 *
 *  scroll – smooth-scrolls to #product-reviews-section with header offset correction.
 *  modal  – moves the canonical [data-product-reviews-root] element into the reviews
 *           modal body before the animation plays, and restores it after the modal
 *           closes, keeping a single copy of the Loox widget in the DOM at all times.
 *
 * The modal open/close mechanics (focus trap, overlay click, ESC key, body scroll
 * lock) are delegated entirely to the existing x-modal / DialogElement implementation
 * in theme.js. This file only handles content migration and scroll interception.
 *
 * NOTE: `dialog:before-show` / `dialog:after-hide` are dispatched without
 * `{ bubbles: true }` in DialogElement, so listeners are attached directly to
 * the modal element rather than delegated on document.
 */

(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Scroll mode – intercept clicks on [data-reviews-display-mode="scroll"]
  // and perform a smooth scroll with a small header offset correction.
  // ---------------------------------------------------------------------------
  document.addEventListener('click', function (event) {
    const trigger = event.target.closest('[data-reviews-display-mode="scroll"]');
    if (!trigger) return;

    event.preventDefault();

    const targetId = (trigger.getAttribute('href') || '#product-reviews-section').replace(/^#/, '');
    const target = document.getElementById(targetId);

    if (!target) return;

    // Respect a sticky header height if the theme exposes one via CSS variable,
    // otherwise fall back to a sensible default offset.
    const headerHeight = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '0',
      10
    );
    const extraOffset = 24; // breathing room above the section

    const top =
      target.getBoundingClientRect().top + window.scrollY - headerHeight - extraOffset;

    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  });

  // ---------------------------------------------------------------------------
  // Modal mode – content migration into / out of the reviews modal.
  //
  // The [data-product-reviews-root] element (the apps section wrapper on the PDP)
  // is moved into [data-reviews-modal-body] before the modal is revealed and
  // moved back to its original position after the modal is hidden.
  //
  // A comment node acts as a lightweight DOM placeholder so the original
  // document position is preserved for the restore step.
  //
  // NOTE: `dialog:before-show` and `dialog:after-hide` do NOT bubble, so we
  // must attach listeners directly to the modal element, not to document.
  // ---------------------------------------------------------------------------

  /** @type {Comment | null} */
  let reviewsPlaceholder = null;

  function initReviewsModal() {
    const modal = document.querySelector('[data-reviews-modal]');
    if (!modal) return;

    /**
     * Move reviews content into the modal body just before the modal opens.
     * At this point the x-modal element is still in its original DOM position
     * (it gets appended to body only after this event fires).
     */
    modal.addEventListener('dialog:before-show', function () {
      const source = document.querySelector('[data-product-reviews-root]');
      const body = modal.querySelector('[data-reviews-modal-body]');

      if (!source || !body) return;
      if (body.contains(source)) return; // guard against double-migration

      // Mark the original position so we can restore after close.
      reviewsPlaceholder = document.createComment('reviews-root-placeholder');
      source.parentNode.insertBefore(reviewsPlaceholder, source);

      body.appendChild(source);
    });

    /**
     * Restore reviews content to its original page position after the modal
     * has closed and the x-modal element has returned to its original parent.
     */
    modal.addEventListener('dialog:after-hide', function () {
      if (!reviewsPlaceholder) return;

      const source = modal.querySelector('[data-product-reviews-root]');

      if (source && reviewsPlaceholder.parentNode) {
        reviewsPlaceholder.parentNode.insertBefore(source, reviewsPlaceholder);
        reviewsPlaceholder.parentNode.removeChild(reviewsPlaceholder);
      }

      reviewsPlaceholder = null;
    });
  }

  // The script loads with `defer`, so the DOM is fully parsed by now.
  // If custom element upgrade is still pending, wait for definition.
  if (customElements.get('x-modal')) {
    initReviewsModal();
  } else {
    customElements.whenDefined('x-modal').then(initReviewsModal);
  }
})();
